import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FormField, FormToggle, ColorPickerField } from '@/shared/components'
import type { Matiere } from '@/shared/types/admin'

const schema = z.object({
  nom: z.string().min(1, 'Le nom est obligatoire'),
  codeMatiere: z.string().min(2, 'Le code doit contenir au moins 2 caractères'),
  couleur: z.string().optional(),
  estLangue: z.boolean(),
})

type FormValues = z.infer<typeof schema>

interface MatiereFormProps {
  matiere?: Matiere
  onSubmit: (data: { nom: string; codeMatiere: string; couleur?: string; estLangue: boolean }) => void
  isPending: boolean
}

export function MatiereForm({ matiere, onSubmit, isPending }: MatiereFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { nom: '', codeMatiere: '', couleur: '', estLangue: false },
  })

  useEffect(() => {
    if (matiere) {
      setValue('nom', matiere.nom)
      setValue('codeMatiere', matiere.codeMatiere)
      setValue('couleur', matiere.couleur || '')
      setValue('estLangue', matiere.estLangue)
    }
  }, [matiere, setValue])

  const handleFormSubmit = (values: FormValues) => {
    onSubmit({
      nom: values.nom.trim(),
      codeMatiere: values.codeMatiere.trim(),
      couleur: values.couleur || undefined,
      estLangue: values.estLangue,
    })
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="mx-auto max-w-lg space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">
          {t('administration.sections.subjects.form.mainInformation', 'Informations principales')}
        </h2>

        <FormField
          label={t('administration.sections.subjects.form.name', 'Nom de la matière')}
          error={errors.nom?.message}
          required
        >
          <input {...register('nom')} className={inputClass} />
        </FormField>

        <FormField
          label={t('administration.sections.subjects.form.code', 'Code')}
          error={errors.codeMatiere?.message}
          helper={t('administration.sections.subjects.form.codeHelper', 'Code unique de la matière (ex: MATH01, ANG02)')}
          required
        >
          <input {...register('codeMatiere')} className={inputClass} />
        </FormField>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">
          {t('administration.sections.subjects.form.appearanceAndType', 'Apparence et Type')}
        </h2>

        <ColorPickerField
          label={t('administration.sections.subjects.form.color', 'Couleur')}
          value={watch('couleur') || ''}
          onChange={(v) => setValue('couleur', v)}
        />

        <FormToggle
          label={t('administration.sections.subjects.form.isLanguage', "C'est une langue")}
          checked={watch('estLangue')}
          onChange={(v) => setValue('estLangue', v)}
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/administration/matieres')}
          className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {t('administration.sections.subjects.form.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending
            ? t('administration.sections.subjects.form.saving', 'Enregistrement...')
            : matiere
              ? t('administration.sections.subjects.form.save', 'Enregistrer')
              : t('administration.sections.subjects.form.create', 'Créer la matière')}
        </button>
      </div>
    </form>
  )
}
